import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  signal,
} from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { LayoutService } from 'src/app/services/layout.service';
import { combineLatest, concat, first, interval, take } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { HeaderComponent } from '@app-core/components/header/header.component';
import { LanguageCodes } from '@app-shared/enums/language-codes.enum';
import { MediaMatcher } from '@angular/cdk/layout';
import { SettingsRepository } from '@app-core/state/settings.repository';
import { ThemeSelector } from '@app-core/enums/themes.enum';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { DevSettingsComponent } from 'src/app/modules/dev-settings/dev-settings.component';
import { SidebarComponent } from 'src/app/modules/sidebar/sidebar.component';
import { SessionRepository } from '@app-core/state/session.repository';
import { LanguageRepository } from '@app-core/state/language.repository';
import { TranslocoService } from '@ngneat/transloco';
import { DateAdapter } from '@angular/material/core';
import { NGQ_LANGUAGES_TOKEN } from '@app-core/injection-tokens/languages.token';
import { ITranslationLanguage } from '@app-core/interfaces/language.interface';
import { FavoriteFiltersService } from 'src/app/entities/favorite-filters/state/favorite-filters.service';
import { NotificationsService } from 'src/app/entities/notifications/state/notifications.service';

import { CountryRepository } from './entities/countries/state/country.repository';
import { FavoriteFiltersRepository } from './entities/favorite-filters/state/favorite-filters.repository';
import { ActivityRepository } from './user-usage/user-usage.repository';

@Component({
  standalone: true,
  selector: 'sandoz-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    RouterOutlet,

    HeaderComponent,
    DevSettingsComponent,
    SidebarComponent,

    MatSidenavModule,
    MatSnackBarModule,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly mediaMatcher = inject(MediaMatcher);
  private readonly snackbar = inject(MatSnackBar);
  private readonly renderer = inject(Renderer2);
  private readonly dateAdapter = inject(DateAdapter);
  private readonly transloco = inject(TranslocoService);

  private readonly settingsRepo = inject(SettingsRepository);
  private readonly languageRepo = inject(LanguageRepository);
  private readonly notificationService = inject(NotificationsService);
  private readonly ffService = inject(FavoriteFiltersService);
  private readonly ffRepo = inject(FavoriteFiltersRepository);
  private readonly activityRepo = inject(ActivityRepository);

  protected readonly sessionRepo = inject(SessionRepository);
  protected readonly countryRepo = inject(CountryRepository);

  protected readonly layoutService = inject(LayoutService);
  protected readonly languagesList =
    inject<ITranslationLanguage[]>(NGQ_LANGUAGES_TOKEN);

  $isLocalhost = signal(false);

  constructor() {
    this.setupServiceWorker();
    this.detectPrefersColorScheme();
    this.applyTheme();

    this.languageRepo.activeLanguageCode$.subscribe(lang_code => {
      if (lang_code) {
        this.transloco.setActiveLang(lang_code);
        this.dateAdapter.setLocale(lang_code);
      }
    });
  }

  ngOnInit(): void {
    window.addEventListener('offline', () => {
      this.snackbar.open('❌ Network connection lost', 'OK', {
        duration: 15000,
      });
    });

    window.addEventListener('online', () => {
      this.snackbar
        .open('✔️ Network connection restored.', 'Refresh Page', {
          duration: 15000,
        })
        .onAction()
        .subscribe(() => location?.reload());
    });

    this.notificationService.init();

    // subscribe to the change of the active country and set app language to a supported language of that
    // country if app language is not previously already set
    this.countryRepo.activeCountry$.subscribe(activeCountry => {
      // if user has no language selected already, select one based on the country
      if (!this.languageRepo.getActiveLanguageCode()) {
        if (activeCountry) {
          // if country is active (it exists / is set), set the first language of that country
          // find if there is a language for that country that is supported in the app
          // check if app language is included in the languages list of the country and return only the first element and get it's code/id
          const supportedLanguageCode: LanguageCodes =
            this.languagesList.filter(appLang =>
              activeCountry.languages
                .map(countryLang => countryLang.short_code)
                .includes(appLang.id),
            )[0]?.id;

          // if one supported language is found, set it as the language of the app
          if (supportedLanguageCode) {
            this.languageRepo.setActiveLanguageByCode(supportedLanguageCode);
          }
        }
      }
    });

    this.ffRepo.resetStore();
    this.ffService.init();

    this.$isLocalhost.set(location.hostname === 'localhost');
  }

  ngOnDestroy(): void {
    // Clean up activity tracking
    this.activityRepo.destroy();
  }

  private setupServiceWorker(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(evt => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            this.snackbar.open('Downloading a new version of the app');
            break;
          case 'VERSION_READY':
            this.snackbar
              .open('Update is available', 'UPDATE', { duration: 0 })
              .onAction()
              .pipe(take(1))
              // eslint-disable-next-line rxjs/no-nested-subscribe
              .subscribe(() => document.location.reload());
            break;
          case 'VERSION_INSTALLATION_FAILED':
            this.snackbar.open('Failed to install a new version', 'OK');
            break;
        }
      });

      // Allow the app to stabilize first, before starting
      // polling for updates with `interval()`.
      const appIsStable$ = this.appRef.isStable.pipe(
        first(isStable => isStable === true),
      );
      const everySixHours$ = interval(6 * 60 * 60 * 1000);
      const everySixHoursOnceAppIsStable$ = concat(
        appIsStable$,
        everySixHours$,
      );

      // eslint-disable-next-line rxjs/no-async-subscribe
      everySixHoursOnceAppIsStable$.subscribe(async () => {
        try {
          const updateFound = await this.swUpdate.checkForUpdate();

          if (updateFound) {
            this.snackbar
              .open('Update is available', 'UPDATE', { duration: 0 })
              .onAction()
              .pipe(take(1))
              // eslint-disable-next-line rxjs/no-nested-subscribe
              .subscribe(() => document.location.reload());
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to check for updates:', err);
        }
      });
    }
  }

  private detectPrefersColorScheme(): void {
    this.settingsRepo.setStoreProp(
      'prefersDark',
      this.mediaMatcher.matchMedia('(prefers-color-scheme: dark)').matches,
    );
    this.mediaMatcher
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', event => {
        this.settingsRepo.setStoreProp('prefersDark', event.matches);
      });
  }

  private applyTheme(): void {
    combineLatest([
      this.settingsRepo.theme$,
      this.settingsRepo.prefersDark$,
    ]).subscribe(([theme, prefersDark]) => {
      if (theme) {
        this.renderer.setAttribute(
          document.documentElement,
          'data-theme',
          theme,
        );
      } else {
        this.renderer.setAttribute(
          document.documentElement,
          'data-theme',
          prefersDark ? ThemeSelector.SANDOZ_DARK : ThemeSelector.SANDOZ_LIGHT,
        );
      }
    });
  }
}
